import storeManager from '../../store/StoreManager';
import { initTimelines, refreshAudioElements, selectElement, setMarkerPosition, setTimelineWidth } from '../../actions/AudioEditorActions';
import { setCurrentSlide } from '../../actions/CurrentSlideActions';
import { updateCurrentSlide } from '../../actions/ProductionActions';
import { changeMainAudioCategoryIndex } from '../../actions/MusicActions';
import audioEditorUtils from '../audioEditor/utils/AudioEditorUtils';
import videoUtils from './VideoUtils';
import previewCommands from './PreviewCommands';
import { changeAudioTabIndex, changeClipartCategory, expandAudioList, hideMenus, showAdvancedModeAlert, showAudioEditor } from '../../actions/MenuActions';
import audioConverter from '../audioEditor/utils/AudioConverter';
import properties from '../../constants/Properties';
import menuType from '../../constants/MenuType';
import modeType from '../../constants/ModeConstants';
import { changeAcoordionWidth } from '../../actions/AccordionActions';
import hoverManager from './HoverManager';
import configuration from '../../constants/Configuration';
import platformDetection from './PlatformDetection';
import slideUtils from './SlidesUtils';
import audioToolUtils from '../audioTool/utils/AudioToolUtils';
import audioVolumeUtils from './AudioVolumeUtils';
import animationManager from './AnimationManager';

const playController = {
    audioEditorWrapper: undefined,
    MARKER_STEP: 10, // miliseconds
    VOLUME_STEP: 50, // miliseconds
    audios: [],
    startDate: -1,
    pauseDate: -1,
    markerTimer: undefined,
    volumeTimer: undefined,
    startTime: -1,
    startTimers: [],
    endTimers: [],
    endOfProductionTimer: undefined,
    startSlideTimers: [],
    endTime: -1,
    refreshControlBar: undefined,
    endSequenceCallback: undefined,
    isPreview: true,
    updateRecordingMenuButtons: undefined,
    slideModels: [],
    isPlaying: false,
    executePlayPause: undefined,
    executeStop: undefined,
    changePlayState: undefined,
    isFirstTimeOpened: false,
    updateScrollForIOS: undefined,
    startScroll: undefined,
    moveScroll: undefined,
    endScroll: undefined,
    isRecording: false,
    oldScrollPosition: -1,
    FADE_LIMIT: 4000, // 4 seconds

    getDispatch() {
        const store = storeManager.getStore();
        return store.dispatch;
    },
    isAudioEditorAvailable() {
        const timelines = this.getAudioTimelines();

        if (timelines == undefined)
            return false;
        else
            return true;
    },
    isAudioEditorMode() {
        if (audioToolUtils.isAudioTool())
            return true;

        var state = storeManager.getState();

        if (state.menu && state.menu.showAudioEditor == true)
            return true;
        else
            return false;
    },
    getAudioTimelines() {
        const state = storeManager.getState();

        if (state.audioEditor == undefined)
            return undefined;

        const timelines = state.audioEditor.timelines;

        return timelines;
    },
    updateAudioElements(offset = 200) {
        this.updateRefreshElementsStatus(true);

        setTimeout(function() {
            this.prepareAudios();
        }.bind(this), offset);
    },
    getCorrectHref(audioItem) {
        if (audioItem.hrefBlob && audioItem.hrefBlob.length > 0)
            return audioItem.hrefBlob;
        else
            return videoUtils.checkCacheBreaker(audioItem.href);
    },
    prepareAudios() {
        if (!this.isAudioEditorAvailable()) {
            this.updateRefreshElementsStatus(false);
            return;
        }

        this.prepareContent();
    },
    useWaveForm() {
        if (platformDetection.isIOS() || platformDetection.isChromeBook() || !audioToolUtils.isAudioEditorTool())
            return false;
        else
            return configuration.USE_WAVEFORM;
    },
    prepareContent() {
        this.pauseAllAudios();
        this.removeAudioElements();

        this.audios = [];

        if (audioVolumeUtils.useVolumeFunctionality())
            audioVolumeUtils.prepareSounds();
        else {
            const wrapper = document.createElement('div');
            this.audioEditorWrapper = wrapper;
            wrapper.style.display = 'none';
            document.body.appendChild(wrapper);
        }

        var timelines = this.getAudioTimelines();
        var i;
        var j;
        var href;

        for (i=0; i<timelines.length; i++) {
            var audioItems = timelines[i].audios;

            for (j=0; j<audioItems.length; j++) {
                href = this.getCorrectHref(audioItems[j]);

                if (audioVolumeUtils.useVolumeFunctionality()) {
                    audioVolumeUtils.loadBufferForUrl(href, audioItems[j].uuid, this.audios);
                } else {
                    if (this.useWaveForm())
                        audioVolumeUtils.loadBufferSound(href);

                    var audioElement = document.createElement('audio');
                    wrapper.appendChild(audioElement);

                    audioElement.src = href;
                    audioElement.preload = 'auto';
                    audioElement.playsInline = true;
                    audioElement.load();

                    this.audios[audioItems[j].uuid] = audioElement;
                }
            }
        }

        this.updateRefreshElementsStatus(false);
    },
    updateRefreshElementsStatus(value) {
        setTimeout(function() {
            var dispatch = this.getDispatch();
            dispatch(refreshAudioElements(value));
        }.bind(this), 5);
    },
    removeAudioElements() {
        var elem = this.audioEditorWrapper;

        if (!elem)
            return;

        var fc = elem.firstChild;

        while(fc) {
            elem.removeChild(fc);
            fc = elem.firstChild;
        }

        if (elem && elem.parentElement)
            elem.parentElement.removeChild(elem);

        this.audioEditorWrapper = undefined;
    },
    setStartTime(isRecording = false) {
        if (this.isAudioEditorMode()) {
            var markerPosition = audioEditorUtils.getMarkerPosition();

            if (markerPosition == -1)
                this.startTime = 0;
            else
                this.startTime = markerPosition;

            if (!isRecording) {
                if (this.startTime >= this.endTime || Math.abs(this.startTime - this.endTime) < 0.01)
                    this.startTime = 0;
            }
        } else { // is Preview/Player or Normal Editor mode
            const state = storeManager.getState();
            const currentSlideIndex = state.currentSlide.currentSlideIndex;
            var slideModel = this.slideModels[currentSlideIndex];

            if (!this.isPreview) // is Normal Editor mode
                this.endTime = slideModel.start + slideModel.duration;

            if (this.startDate != -1 && this.pauseDate != -1) {
                var timeOffset = this.pauseDate - this.startDate;

                this.startTime = this.startTime + timeOffset / 1000;
            } else
                this.startTime = slideModel.start;
        }

        this.startDate = new Date().getTime();
    },
    playMarker(isRecording = false) {
        if (isRecording) {
            return; // remove for all because of CH-846

            if (audioToolUtils.isAudioTool()) // remove for AE tool
                return;

            if (platformDetection.isTouchableDevice()) // remove for Cartoonist for touchable devices
                return;
        }

        this.markerTimer = setInterval(this.checkMarkerPosition.bind(this), this.MARKER_STEP);
    },
    checkMarkerPosition() {
        const dispatch = this.getDispatch();
        var offset = (new Date().getTime() - this.startDate) / 1000;
        var newPos = this.startTime + offset;

        if (this.startTime  == -1) {
            this.clearTimers();

            return;
        }

        dispatch(setMarkerPosition(newPos));

        if (this.isRecording) {
            if (audioEditorUtils.getSlideWidth(newPos) >= (storeManager.getState().audioEditor.timelineWidth - 20)) {
                // dispatch(setTimelineWidth(audioEditorUtils.getSlideWidth(newPos + 60)));
            } else {
                if (!audioToolUtils.isAudioTool())
                    this.checkScrollPosition(newPos);
            }
        } else
            this.checkScrollPosition(newPos);
    },
    getIpadScrollPosition() {
        const scrollComponent = document.getElementsByClassName('scrollForIOS')[0];

        if (scrollComponent) {
            var leftPos = scrollComponent.style.left;
            var pos = parseFloat(leftPos.replace('px', '')) * -1;

            if (pos >= 0)
                return pos;
            else
                return 0;
        } else
            return 0;
    },
    getNormalScrollPosition() {
        var comp = document.getElementById('slidesList');
        var scrollPos = 0;

        if (comp)
            scrollPos = comp.scrollLeft;

        return scrollPos;
    },
    getScrollPosition() {
        if (platformDetection.isTouchableDevice())
            return this.getIpadScrollPosition();
        else
            return this.getNormalScrollPosition();
    },
    updateScrollPosition(offsetTime = 300, useOldScrollPosition = false) {
        if (!this.isAudioEditorMode() || slideUtils.startPosition != -1)
            return;

        setTimeout(function () {
            if (useOldScrollPosition && this.oldScrollPosition != -1)
                this.goToOldScrollPosition();
            else {
                var newPos = audioEditorUtils.getMarkerPosition();
                this.checkScrollPosition(newPos, true);
            }
        }.bind(this), offsetTime);
    },
    goToOldScrollPosition() {
        this.setScrollPosition(this.oldScrollPosition);
    },
    getOldScrollPosition() {
        this.oldScrollPosition = this.getScrollPosition();
    },
    checkScrollPosition(newPos, forceUpdate = false) {
        var scrollPos = this.getScrollPosition();
        var state = storeManager.getState();
        var accordionWidth = state.accordion.width;
        var windowWidth = state.production.windowWidth;

        if (accordionWidth == undefined)
            accordionWidth = properties.BROWSER_WIDTH;

        if (!(windowWidth > 0))
            windowWidth = window.innerWidth;

        var visibleWidth = windowWidth - audioToolUtils.buttonsHolderWidth() - accordionWidth;
        var currentPos = audioEditorUtils.getSlideWidth(newPos);

        if (forceUpdate)
            this.setScrollPosition(currentPos - visibleWidth / 2);
        else {
            if (Math.abs(currentPos - scrollPos) > visibleWidth / 2)
                this.setScrollPosition(currentPos - visibleWidth / 2);
        }
    },
    setScrollPosition(position) {
        if (platformDetection.isTouchableDevice()) {
            if (this.updateScrollForIOS)
                this.updateScrollForIOS(position);
        } else {
            var comp = document.getElementById('slidesList');

            if (comp)
                comp.scrollLeft = position;
        }
    },
    resetScrollPosition() {
        this.setScrollPosition(0);
    },
    getAvailableTimelines(timelines, isRecording = false) {
        if (isRecording)
            return [];

        var i;
        var availableTimelines = [];

        for (i=0; i<timelines.length; i++) {
            var timeline = timelines[i];

            if (timeline.isSolo == true) {
                return [i];
            } else
                if (timeline.isMute == true)
                    continue;

            availableTimelines.push(i);
        }

        return availableTimelines;
    },
    play(isPreview = false, isRecording = false) { // isRecording is currently unused since we removed play functionality while recording (CART-1370)
        if (audioToolUtils.isAudioTool() && audioEditorUtils.getTimelinesMaxPosition() == 0 && !isRecording)
            return;

        if (!audioToolUtils.isAudioTool() && this.isAudioEditorMode())
            videoUtils.checkVideoList();

        this.isRecording = isRecording;
        this.isPreview = isPreview;
        this.clearAll(true);

        this.slideModels = this.getSlideModels();
        this.setStartTime(isRecording);

        var timelines = this.getAudioTimelines();
        var avaialableTimelines = this.getAvailableTimelines(timelines, isRecording);

        if (!this.isPreview && this.isAudioEditorMode()) // in Player/Preview the marker shouldn't be updated
            this.playMarker(isRecording);

        for (var i=0; i<timelines.length; i++) {
            if (avaialableTimelines.indexOf(i) != -1)
                this.playTimelineSequence(timelines[i].audios, timelines[i].volume);
        }

        this.playSlidesSequence(true, -1, isRecording);

        if (!isRecording)
            this.startVolumeTimer();

        this.isPlaying = true;
    },
    getSlideModels() {
        if (audioToolUtils.isAudioTool()) {
            if (!this.isRecording) {
                this.endTime = audioEditorUtils.getTimelinesMaxPosition();
                var markerPosition = audioEditorUtils.getMarkerPosition();

                if (markerPosition >= this.endTime || Math.abs(markerPosition - this.endTime) < 0.1)
                    markerPosition = 0;

                this.endOfProductionTimer = setTimeout(function () {
                    this.endOfProduction();
                }.bind(this), (this.endTime - markerPosition) * 1000);
            }

            return [];
        }

        const state = storeManager.getState();
        const slides = state.production.slides;
        const currentSlide = state.currentSlide;
        var i;
        var currentTime = 0;
        var slide;
        var model;
        var videoModel;
        var models = [];

        for (i=0; i<slides.length; i++) {
            slide = slides[i];
            model = {};

            if (i == currentSlide.currentSlideIndex) {
                model.duration = currentSlide.model.time;
                videoModel = videoUtils.getSlideVideoModel(currentSlide.model);
            } else {
                model.duration = slide.time;
                videoModel = videoUtils.getSlideVideoModel(slide);
            }

            model.start = currentTime;

            if (videoModel)
                model.videoModel = videoModel;

            currentTime = currentTime + model.duration;

            models.push(model);
        }

        var timelinesMaxPos = audioEditorUtils.getTimelinesMaxPosition();

        if (timelinesMaxPos > currentTime)
            currentTime = timelinesMaxPos;

        this.endTime = currentTime;

        return models;
    },
    playSlidesSequence(startPlaying = true, markerPos = -1, isRecording = false) {
        if (audioToolUtils.isAudioTool())
            return;

        this.updateCurrentSlideProperties();

        var slideModels = this.slideModels;

        if (!startPlaying)
            slideModels = this.getSlideModels();

        var i;
        var slideModel;
        var startIndex = -1;
        var id;
        var markerPosition = this.startTime;
        var forceMute = isRecording; // while recording the video volume should be zero

        if (!startPlaying) {
            if (markerPos > -1)
                markerPosition = markerPos;
            else
                markerPosition = audioEditorUtils.getMarkerPosition();
        }

        for (i=0; i<slideModels.length; i++) {
            slideModel = slideModels[i];

            if (markerPosition >= slideModel.start && markerPosition < (slideModel.start + slideModel.duration)) { // start slide
                startIndex = i;

                if (this.isPreview || this.isAudioEditorMode())
                    this.goToSlideIndex(i);

                this.prepareVideo(slideModel, markerPosition, startPlaying, forceMute, i);

                if (!startPlaying) // when just click on the ruler bar to change the marker position
                    return;
                else {
                    if (!this.isPreview && !this.isAudioEditorMode()) // is normal mode
                        break;
                    else
                        continue;
                }
            }

            if (startIndex > -1 && i > startIndex) {
                //set start timer
                id = setTimeout(function (index, model) {
                    if (!audioToolUtils.isAudioTool()) {
                        videoUtils.pauseVideoPreview(false);

                        if (!videoUtils.isVideoBackground() && videoUtils.isVideoShapes())
                            videoUtils.pauseVideoShapesPreview(false);
                    }

                    this.goToSlideIndex(index);
                    this.prepareVideo(model, -1, startPlaying, forceMute, index);
                }.bind(this), (slideModel.start - markerPosition) * 1000, i, slideModel);

                this.startSlideTimers.push(id);
            }
        }

        if (!isRecording) {
            this.endOfProductionTimer = setTimeout(function () {
                this.endOfProduction();
            }.bind(this), (this.endTime - markerPosition) * 1000);
        }
    },
    prepareVideo(slideModel, markerPosition, startPlaying, forceMute = false, index = -1) {
        if (audioToolUtils.isAudioTool())
            return;

        var offset = 0;

        if (markerPosition > 0)
            offset = markerPosition - slideModel.start;

        videoUtils.prepareVideo(slideModel.videoModel, offset, startPlaying, forceMute, index);
        animationManager.startAnimations(false, offset * 1000, startPlaying);
    },
    updateCurrentSlideProperties() {
        if (audioToolUtils.isAudioTool())
            return;

        const dispatch = this.getDispatch();
        dispatch(updateCurrentSlide());
    },
    goToSlideIndex(index) {
        if (audioToolUtils.isAudioTool())
            return;

        const dispatch = this.getDispatch();
        dispatch(setCurrentSlide(index));
    },
    getTotalVolume() {
        if (audioToolUtils.isAudioPlayer()) {
            var value = storeManager.getState().audioEditor.volume;

            if (value == undefined)
                value = 0.5;

            return value;
        } else
            return 1.0;
    },
    playTimelineSequence(audioItems, timelineVolume) {
        if (audioItems == undefined || audioItems.length < 1)
            return;

        var i;
        var audio;
        var markerPosition = this.startTime;

        for (i=0; i<audioItems.length; i++) {
            audio = audioItems[i];

            var timeoutStartPosition = audio.left - markerPosition;
            var id;
            var audioElement = this.audios[audio.uuid];

            if (!audioElement)
                continue;

            if (audioVolumeUtils.useVolumeFunctionality())
                audioVolumeUtils.changeVolume(audio.uuid, 0);
            else
                audioElement.volume = 0;

            if (timeoutStartPosition >= 0) { // the marker is on the left of audio item
                if (audio.inPoint == undefined)
                    audioElement.currentTime = 0;
                else
                    audioElement.currentTime = audio.inPoint; // this should be set in seconds

                //set start timer
                id = setTimeout(function (audioItem) {
                        var localAudio = this.audios[audioItem.uuid];

                        try {
                            if (localAudio) {
                                if (audioVolumeUtils.useVolumeFunctionality())
                                    localAudio.play(0, localAudio.currentTime);
                                else
                                    localAudio.play();
                            }
                        } catch (e) {
                            // error
                        }
                }.bind(this), timeoutStartPosition * 1000, audio);

                this.startTimers.push(id);

                //set end timer
                id = setTimeout(function (audioItem) {
                    var localAudio = this.audios[audioItem.uuid];

                    if (localAudio) {
                        if (audioVolumeUtils.useVolumeFunctionality())
                            audioVolumeUtils.refreshSource(this.audios, audioItem.uuid);
                        else
                            localAudio.pause();
                    }
                }.bind(this), (timeoutStartPosition + audio.duration) * 1000, audio);

                this.endTimers.push(id);
            } else
            if ((audio.left + audio.duration) > markerPosition) { // the marker is in the middle of audio item
                if (audio.inPoint == undefined)
                    audioElement.currentTime = (markerPosition - audio.left);
                else
                    audioElement.currentTime = audio.inPoint + (markerPosition - audio.left);

                if (audioVolumeUtils.useVolumeFunctionality())
                    audioElement.play(0.01, audioElement.currentTime);
                else
                    audioElement.play();

                // set end timer
                id = setTimeout(function (audioItem) {
                    var localAudio = this.audios[audioItem.uuid];

                    if (localAudio) {
                        if (audioVolumeUtils.useVolumeFunctionality())
                            audioVolumeUtils.refreshSource(this.audios, audioItem.uuid);
                        else
                            localAudio.pause();
                    }
                }.bind(this), (audio.left + audio.duration - markerPosition) * 1000, audio);

                this.endTimers.push(id);
            }
        }
    },
    pause(updateScroll = false) {
        this.pauseDate = new Date().getTime();
        this.clearAll(true);

        if (updateScroll)
            this.updateScrollPosition(300, true);
    },
    stop() {
        this.clearAll();

        if (!this.isPreview) {
            var dispatch = this.getDispatch();
            dispatch(setMarkerPosition(0));
        }

        this.resetProperties(true);
    },
    clearAll(isPlay = false, resetTime = false) {
        if (!audioToolUtils.isAudioTool()) {
            videoUtils.pauseVideoPreview(resetTime);

            if (!videoUtils.isVideoBackground() && videoUtils.isVideoShapes())
                videoUtils.pauseVideoShapesPreview(resetTime);
        }

        if (this.isPreview && this.refreshControlBar)
            this.refreshControlBar();

        this.pauseAllAudios();
        this.clearTimers();

        this.isPlaying = false;

        if (isPlay)
            animationManager.stopAnimations(false, false);
        else {
            if (animationManager.isAnimationMode())
                animationManager.stopAnimationIndEditMode();
            else
                animationManager.stopAnimations();
            
            videoUtils.refreshVideosInPlayMode();
        }
    },

    pauseAllAudios() {
        var key;

        for (key in this.audios) {
            if (audioVolumeUtils.useVolumeFunctionality())
                audioVolumeUtils.refreshSource(this.audios, key);
            else
                this.audios[key].pause();
        }
    },
    clearTimers() {
        var i;

        for (i=0; i<this.startTimers.length; i++)
            clearTimeout(this.startTimers[i]);

        for (i=0; i<this.endTimers.length; i++)
            clearTimeout(this.endTimers[i]);

        for (i=0; i<this.startSlideTimers.length; i++)
            clearTimeout(this.startSlideTimers[i]);

        if (this.markerTimer)
            clearInterval(this.markerTimer);

        if (this.volumeTimer)
            clearInterval(this.volumeTimer);

        if (this.endOfProductionTimer)
            clearTimeout(this.endOfProductionTimer);

        this.startTimers = [];
        this.endTimers = [];
        this.startSlideTimers = [];
    },
    restart() {
        if (audioToolUtils.isAudioTool())
            return;

        this.goToSlideIndex(0);

        setTimeout(function() {videoUtils.goToStartPosition();}.bind(this), 10);
    },
    endOfProduction(startModule = false) {
        if (audioToolUtils.isAudioTool()) {
            if (this.updateRecordingMenuButtons)
                this.updateRecordingMenuButtons();

            this.clearAll();
            this.resetProperties(false);
            return;
        }

        if (this.isPreview) { // is PREVIEW or PLAYER mode
            previewCommands.isPlaying = false;

            if (this.endSequenceCallback)
                this.endSequenceCallback();

            if (!startModule) // when open audio editor module then should keep the current slide
                this.restart(); // should go back to the first slide

            this.clearAll();
        } else { // is recording menu or Advanced mode
            if (this.updateRecordingMenuButtons)
                this.updateRecordingMenuButtons();

            if (this.isAudioEditorMode() && !startModule)
                this.clearAll(false, true);
            else
                this.clearAll();
        }

        this.resetProperties(startModule);
    },
    resetProperties(isStop = false, forceVideoUpdate = false) {
        this.startDate = -1;
        this.pauseDate = -1;
        this.startTime = -1;

        this.startTimers = [];
        this.endTimers = [];
        this.startSlideTimers = [];
        this.markerTimer = undefined;
        this.volumeTimer = undefined;
        this.endOfProductionTimer = undefined;

        if (forceVideoUpdate) { // exit from AudioEditor mode to normal mode
            if (!audioToolUtils.isAudioTool())
                videoUtils.goToStartPosition();

            return;
        }

        if (!this.isPreview) {
            if (!this.isAudioEditorMode()) {
                if (!audioToolUtils.isAudioTool())
                    videoUtils.goToStartPosition();
            } else {
                if (isStop) {
                    if (!audioToolUtils.isAudioTool()) {
                        this.goToSlideIndex(0);
                        videoUtils.goToStartPosition();
                    }

                    this.resetScrollPosition();
                }
            }
        }
    },
    resetPropertiesForPreview() {
        this.clearAll();
        this.resetProperties();
    },
    changeMode(startModule = false) {
        this.isPreview = false;

        if (this.isAudioEditorAvailable()) {
            this.updateCurrentSlideProperties();
            this.clearAll();
            this.resetProperties(false, true);
            this.endOfProduction(startModule);

            const dispatch = this.getDispatch();
            dispatch(setMarkerPosition(0));
        }

        videoUtils.prepareMultipleVideos(startModule);
        animationManager.stopAnimations();
    },
    checkAudioEditorMode() {
        if (audioToolUtils.isAudioTool())
            return;

        var dispatch = this.getDispatch();

        if (!this.isAudioEditorAvailable())
            dispatch(showAdvancedModeAlert(true));
        else
            this.toggleAudioEditorMode();
    },
    checkAudioEditorModeWhenOpenAudioMenus(menuType = 0) { // 0 - story sound, 1 - slide sound, 2 - recording sound
        this.openAudioTab(menuType);

        if (this.isAudioEditorAvailable() && !this.isAudioEditorMode())
            this.toggleAudioEditorMode(true, false);
    },
    openAudioTab(menuType) {
        var dispatch = this.getDispatch();

        if (!this.isAudioEditorAvailable()) {
            if (menuType == 0 || menuType == 1)
                dispatch(changeAudioTabIndex(menuType));
        }

        if (menuType == 0 || menuType == 1)
            dispatch(changeClipartCategory(2));

        videoUtils.executeAction('reset');
    },
    toggleAudioEditorMode(forceToOpen = false, hidePopups = true) {
        var dispatch = this.getDispatch();

        dispatch(changeAcoordionWidth(properties.BROWSER_WIDTH));

        var value = storeManager.getState().menu.showAudioEditor;

        if (value == undefined)
            value = false;

        this.changeMode(!value);

        if (!this.isAudioEditorAvailable())
            this.convertStructure();

        if (forceToOpen)
            value = false;

        if (!value) {
            setTimeout(function() {
                dispatch(setTimelineWidth(audioEditorUtils.getTimelineWidth() + audioEditorUtils.getTimelineOffset()));
            }.bind(this), 8);
        }

        setTimeout(function() {

            if (value == true)
                this.closeAudioTab();

            dispatch(showAudioEditor(!value));

            hoverManager.hide();

            if (value == false) { // enter Advanced mode
                this.prepareAudios();

                setTimeout(function() {
                    var newPos = audioEditorUtils.getMarkerPositionForSlideIndex();
                    dispatch(setMarkerPosition(newPos));
                    this.checkScrollPosition(newPos);

                    var isSearchContext = (storeManager.getState().menu.contextMode == modeType.SEARCH_CONTEXT);

                    if (hidePopups && !isSearchContext)
                        dispatch(hideMenus(menuType.ALL_MENUS));

                    this.isAudioTabFirstTimeOpened();
                }.bind(this), 100);
            }
        }.bind(this), 10);
    },
    isAudioTabFirstTimeOpened(showAudioEditor) {
        var showAudioEditor = this.isAudioEditorMode();

        if (showAudioEditor && !this.isFirstTimeOpened) {
            setTimeout(function() {
                var dispatch = this.getDispatch();

                dispatch(expandAudioList(true, 'main'));

                this.isFirstTimeOpened = true;
            }.bind(this), 5);
        }
    },
    prepareAudioTool() {
        const dispatch = this.getDispatch();

        if (audioToolUtils.isAudioPlayer()) {
            dispatch(setMarkerPosition(0));
            this.updateAudioElements();
        } else {
            dispatch(setMarkerPosition(0));

            dispatch(setTimelineWidth(audioEditorUtils.getTimelineWidth() + audioEditorUtils.getTimelineOffset()));
            this.prepareAudios();

            dispatch(expandAudioList(true, 'main'));
            dispatch(changeClipartCategory(2));

            if (!platformDetection.isIOS())
                dispatch(changeMainAudioCategoryIndex(0));

            setTimeout(function() {
                audioEditorUtils.addActionToHistory(); // add first state to UNDO history
            }.bind(this), 50);
        }
    },
    closeAudioEditorMode() {
        if (this.isAudioEditorAvailable()) {
            var dispatch = this.getDispatch();
            dispatch(showAudioEditor(false));
            this.closeAudioTab();
        }
    },
    closeAudioTab() {
        var dispatch = this.getDispatch();
        dispatch(changeClipartCategory(0));
    },
    convertStructure() {
        this.updateCurrentSlideProperties();

        setTimeout(function() {
            var dispatch = this.getDispatch();
            var data = audioConverter.convert(storeManager.getState().production.slides, storeManager.getState().production.storySound);

            if (configuration.LOAD_LOCAL)
                data = this.getEmptyTimelines();

            dispatch(initTimelines(data));
        }.bind(this), 4);
    },
    getEmptyTimelines() {
        var timelines = [];

        // timelines.push({index: 0, volume: 1.0, audios: [{points: [{time:0, vol:1}, {time:1, vol:0.5}, {time:2, vol:0.8}, {time:4, vol:0.2}], uuid: uuid.v4(), title:'Song 1', href: 'https://d3b76ijxom7qfl.cloudfront.net/movieaudioeditor/1837740.mp3', left: 0, duration: 4.0, inPoint: 1.5238095238095237}]});
        timelines.push({index: 0, volume: 1.0, audios: []});
        timelines.push({index: 1, volume: 1.0, audios: []});
        timelines.push({index: 2, volume: 1.0, audios: []});

        if (audioToolUtils.isAudioTool()) {
            timelines.push({index: 3, volume: 1.0, audios: []});
            timelines.push({index: 4, volume: 1.0, audios: []});

            if (platformDetection.isIOS() && !platformDetection.isIOSApp()) { // is IOS browser but not the app
                // not include the last timeline
            } else {
                if (window.innerHeight >= 610)
                    timelines.push({index: 5, volume: 1.0, audios: []});
            }
        }

        return {timelines: timelines};
    },
    executeAction(action) {
        switch (action) {
            case 'play': {
                if (this.executePlayPause)
                    this.executePlayPause();
                break;
            }

            case 'stop': {
                if (this.executeStop)
                    this.executeStop();
                break;
            }

            case 'left': {
                this.changeSelection();
                break;
            }

            case 'right': {
                this.changeSelection(false);
                break;
            }
        }
    },
    changeSelection(isLeft = true) {
        var timelines = storeManager.getState().audioEditor.timelines;
        var i;

        for (i=0; i<timelines.length; i++) {
            if (timelines[i].isSelected && timelines[i].audios.length > 1) {
                var audios = timelines[i].audios;
                var j;
                var position = [];
                var map = [];
                var currentTarget = -1;

                for (j=0; j<audios.length; j++) {
                    position.push(audios[j].left);
                    map[audios[j].left] = audios[j].uuid;

                    if (audios[j].showControlPoint)
                        currentTarget = audios[j].left;
                }

                position.sort(function(a, b){return a - b});

                if (currentTarget != -1) {
                    var index = position.indexOf(currentTarget);

                    if (isLeft)
                        index = index - 1;
                    else
                        index = index + 1;

                    if (index >= 0 && index < audios.length) {
                        var newUuid = map[position[index]];
                        var dispatch = this.getDispatch();

                        if (newUuid)
                            dispatch(selectElement(newUuid, true));
                    }
                }
            }
        }
    },

    // VOLUME functionality
    getStartVolumeForItem(item) {
        if (item.points && item.points[0])
            return item.points[0].vol;
        else
            return 1;
    },
    startVolumeTimer() {
        this.volumeTimer = setInterval(this.checkVolume.bind(this), this.VOLUME_STEP);
    },
    checkVolume() {
        var offset = (new Date().getTime() - this.startDate) / 1000;
        var newPos = this.startTime + offset;

        if (this.startTime  == -1) {
            this.clearTimers();

            return;
        }

        this.setCorrectVolumeForPosition(newPos);
    },
    setCorrectVolumeForPosition(position) {
        var timelines = this.getAudioTimelines();
        var i;

        for (i=0; i<timelines.length; i++) {
            var audios = timelines[i].audios;
            var j;
            var timelineVolume = timelines[i].volume * this.getTotalVolume();

            if (!(timelineVolume >= 0 && timelineVolume <= 1))
                timelineVolume = 1;

            for (j=0; j<audios.length; j++) {
                if (audios[j].left <= position && position <= (audios[j].left + audios[j].duration))
                    this.setVolumeForAudio(audios[j], position, timelineVolume);
            }
        }
    },
    setVolumeForAudio(audio, position, timelineVolume) {
        if (!this.audios)
            return;

        var audioElement = this.audios[audio.uuid];

        if (!audioElement)
            return;

        var volume = 1;
        var i;
        var localPosition = position - audio.left;

        if (audio.points) {
            for (i=0; i<audio.points.length; i++) {
                if (audio.points[i].time == localPosition) {
                    volume = audio.points[i].vol;
                    break;
                }

                if (audio.points[i].time > localPosition && i > 0) {
                    volume = audioEditorUtils.getIntersectionPoint(audio.points[i-1].time, audio.points[i-1].vol, audio.points[i].time, audio.points[i].vol, localPosition);

                    if (!(volume >= 0 && volume <= 1))
                        volume = 1;

                    break;
                }
            }
        }

        volume = volume * timelineVolume;
        var finalVolume = this.applyFadeOut(volume, position);

        if (audioVolumeUtils.useVolumeFunctionality())
            audioVolumeUtils.changeVolume(audio.uuid, finalVolume);
        else
            audioElement.volume = finalVolume;
    },
    applyFadeOut(volume, position) {
        return volume;

        if (!this.isPreview) // fade out functionality should be available only for PREVIEW or PLAYER mode.
            return volume;

        var endTime = this.endTime;
        var fadeDuration = this.FADE_LIMIT / 1000;
        var remainingTime = endTime - position;

        if (remainingTime > fadeDuration)
            return volume;

        var fadePercent = remainingTime / fadeDuration;

        return volume * fadePercent;
    }
};

export default playController;



/** WEBPACK FOOTER **
 ** ./src/components/utils/PlayController.js
 **/